import { useMemo } from "react";

type Props = {
  /** Points to draw. Length doesn't matter — we'll fit them into the box. */
  points: number[];
  width?: number;
  height?: number;
  /** "up" | "down" | "flat" — drives the stroke color. */
  direction?: "up" | "down" | "flat";
  /** Show a filled gradient under the line. */
  fill?: boolean;
  className?: string;
  /** Width of the line stroke in px. */
  strokeWidth?: number;
};

const COLOR = {
  up:   { stroke: "var(--color-emerald)",  fill: "rgba(127,255,140,0.15)" },
  down: { stroke: "var(--color-red)",      fill: "rgba(255, 71, 87,0.15)" },
  flat: { stroke: "rgba(255,255,255,0.5)", fill: "rgba(255,255,255,0.05)" },
};

/**
 * Hand-rolled SVG sparkline. Used on every asset row. Hardware-cheap,
 * sub-1KB DOM cost. The direction prop drives color (uptick green,
 * downtick red), inferred by caller from first/last delta.
 */
export function Sparkline({
  points,
  width = 80,
  height = 24,
  direction = "flat",
  fill = true,
  className,
  strokeWidth = 1.25,
}: Props) {
  const { d, fillD } = useMemo(() => {
    if (!points.length) return { d: "", fillD: "" };
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;
    const stepX = points.length > 1 ? width / (points.length - 1) : width;
    const pad = strokeWidth;
    const usableH = height - pad * 2;

    const coords = points.map((v, i) => {
      const x = i * stepX;
      const y = pad + (1 - (v - min) / span) * usableH;
      return [x, y] as const;
    });

    const dPath = coords
      .map(([x, y], i) => (i === 0 ? `M${x.toFixed(2)},${y.toFixed(2)}` : `L${x.toFixed(2)},${y.toFixed(2)}`))
      .join(" ");

    const fillPath = `${dPath} L${width},${height} L0,${height} Z`;
    return { d: dPath, fillD: fillPath };
  }, [points, width, height, strokeWidth]);

  const c = COLOR[direction];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden
    >
      {fill && d && <path d={fillD} fill={c.fill} />}
      {d && (
        <path
          d={d}
          fill="none"
          stroke={c.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
